import Axhelper from '@/lib/Axhelper'

/**
 * KorektyService - Handles all korekty-related API operations
 * Separates API logic from React hooks and components
 */
class KorektyService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			invoices: `/api/korekty/filters`,
			invoicesDetails: `/api/korekty/details`,
			invoicesDeleteOne: `/api/korekty/`,
			invoicesDeleteMany: `/api/korekty/deletemany`,
			invoicesCreate: `/api/korekty/`,
			invoicesUpdate: `/api/korekty/`,
			invoicesByCustomer: `/api/korekty/bycustomer`,
			invoicesDownload: `/api/korekty/download`,
			assignCustomer: `/api/infakt/assigncorrectioncustomer`,
			assignProduct: `/api/infakt/assigncorrectionproduct`,
			checkApi: `/api/infakt/checkapi`,
			pullSynchronize: `/api/infakt/pullsynchronizecorrections`,
			pushToInfakt: `/api/infakt/pushtokorekta`,
		}
	}

	async getKorektaById(korektaId) {
		try {
			if (!korektaId) {
				return { data: null, error: 'Invalid korekta ID provided' }
			}

			const url = `${this.endpoints.invoicesDetails}/${encodeURIComponent(korektaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: null, error: response.error }
			}

			return { data: response.data || null, error: null }
		} catch (error) {
			console.error('KorektyService.getKorektaById error:', error)
			return { data: null, error: error.message || 'Failed to fetch korekta' }
		}
	}

	async getKorektyWithFilters(options = {}) {
		try {
			const { limit = 50, sortColumn = 'invoiceDate', sortDirection = 'desc', filters = {} } = options

			const url = this._buildFilteredUrl({
				limit,
				sortColumn,
				sortDirection,
				filters,
			})

			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			// Parse pagination info from headers
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			const hasMore = response.data?.length < totalCount

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore,
				headers: response.headers,
			}
		} catch (error) {
			console.error('KorektyService.getKorektyWithFilters error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch korektas',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	/**
	 * Delete multiple invoices
	 * @param {Array<string>} korektaIds - Array of korekta IDs to delete
	 * @returns {Promise<{success: boolean, error: string|null, message: string}>}
	 */
	async deleteKorekty(korektaIds) {
		try {
			const ids = [...new Set((korektaIds || []).map(id => String(id)).filter(Boolean))]
			if (!ids.length) {
				return { success: false, error: 'Invalid korekta IDs provided', message: null }
			}

			if (ids.length === 1) {
				const url = `${this.endpoints.invoicesDeleteOne}${ids[0]}`
				const res = await Axhelper.sendDeleteAsync(url)
				if (res?.error) return { success: false, error: res.error, message: null }
				return { success: true, error: null, message: 'Invoice has been deleted successfully.' }
			}

			try {
				const bulkJson = await Axhelper.sendPostAsync(this.endpoints.invoicesDeleteMany, { ids })
				if (!bulkJson?.error) {
					return { success: true, error: null, message: 'Selected invoices have been deleted successfully.' }
				}
				console.warn('[KorektyService] Bulk JSON delete failed:', bulkJson.error)
			} catch (e) {
				console.warn('[KorektyService] Bulk JSON delete threw:', e)
			}

			try {
				const form = new URLSearchParams()
				ids.forEach(id => form.append('ids[]', id))
				const bulkForm = await Axhelper.sendPostAsync(this.endpoints.invoicesDeleteMany, form)
				if (!bulkForm?.error) {
					return { success: true, error: null, message: 'Selected invoices have been deleted successfully.' }
				}
				console.warn('[KorektyService] Bulk FORM delete failed:', bulkForm.error)
			} catch (e) {
				console.warn('[KorektyService] Bulk FORM delete threw:', e)
			}

			const results = await Promise.allSettled(
				ids.map(id => Axhelper.sendDeleteAsync(`${this.endpoints.invoicesDeleteOne}${id}`))
			)
			const failed = results
				.map((r, i) => (r.status === 'fulfilled' && !r.value?.error ? null : ids[i]))
				.filter(Boolean)

			if (failed.length) {
				return { success: false, error: `Failed to delete ids: ${failed.join(', ')}`, message: null }
			}

			return { success: true, error: null, message: 'Selected invoices have been deleted successfully.' }
		} catch (error) {
			console.error('KorektyService.deleteKorekty error:', error)
			return { success: false, error: error?.message || 'Failed to delete invoices', message: null }
		}
	}

	async createKorekta(invoiceData) {
		// console.log(invoiceData)
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.invoicesCreate, invoiceData)
			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}
			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.createKorekta error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create invoice',
			}
		}
	}

	async updateKorekta(korektaId, invoiceData) {
		try {
			if (!korektaId) {
				return {
					success: false,
					error: 'Invalid korekta ID provided',
				}
			}

			const url = `${this.endpoints.invoicesUpdate}/${encodeURIComponent(korektaId)}`
			const response = await Axhelper.sendPutAsync(url, invoiceData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.updateKorekta error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update invoice',
			}
		}
	}

	/**
	 * Validate Infakt API key
	 * GET /api/infakt/checkapi?apikey=:apiKey
	 * @param {string} apiKey
	 * @returns {Promise<{success: boolean, error: string|null, data: *}>}
	 */
	async checkApi(apiKey) {
		try {
			if (!apiKey?.toString().trim()) {
				return {
					success: false,
					error: 'API key is required',
					data: null,
				}
			}

			const url = `${this.endpoints.checkApi}?apikey=${encodeURIComponent(apiKey.trim())}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.checkApi error:', error)
			return {
				success: false,
				error: error.message || 'Failed to check Infakt API key',
				data: null,
			}
		}
	}

	/**
	 * Pull-synchronize Infakt data for a company
	 * GET /api/infakt/pullsynchronize/:companyId
	 * @param {string} companyId
	 * @returns {Promise<{success: boolean, error: string|null, data: *}>}
	 */
	async pullSynchronize(companyId) {
		try {
			if (!companyId) {
				return {
					success: false,
					error: 'Company ID is required',
					data: null,
				}
			}

			const url = `${this.endpoints.pullSynchronize}/${encodeURIComponent(companyId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.pullSynchronize error:', error)
			return {
				success: false,
				error: error.message || 'Failed to synchronize Infakt data',
				data: null,
			}
		}
	}

	/**
	 * Push invoice to Infakt
	 * POST /api/infakt/pushtokorekta/:korektaId
	 * @param {string} korektaId
	 * @returns {Promise<{success: boolean, error: string|null, data: *}>}
	 */
	async pushToInfakt(korektaId) {
		try {
			if (!korektaId) {
				return {
					success: false,
					error: 'Korekta ID is required',
					data: null,
				}
			}

			const url = `${this.endpoints.pushToInfakt}/${encodeURIComponent(korektaId)}`
			const response = await Axhelper.sendPostAsync(url, {})

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.pushToInfakt error:', error)
			return {
				success: false,
				error: error.message || 'Failed to push korekta to Infakt',
				data: null,
			}
		}
	}

	/**
	 * Assign placowka (customer) to an Infakt invoice
	 * PUT /api/infakt/assigncorrectioncustomer/:korektaId/:placowkaId
	 * @param {string} korektaId
	 * @param {string} placowkaId
	 * @returns {Promise<{success: boolean, error: string|null, data: *}>}
	 */
	async assignCustomer(korektaId, placowkaId) {
		try {
			if (!korektaId || !placowkaId) {
				return {
					success: false,
					error: 'Korekta ID and placowka ID are required',
					data: null,
				}
			}

			const url = `${this.endpoints.assignCustomer}/${encodeURIComponent(korektaId)}/${encodeURIComponent(placowkaId)}`
			const response = await Axhelper.sendPutAsync(url, null)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.assignCustomer error:', error)
			return {
				success: false,
				error: error.message || 'Failed to assign placowka to invoice',
				data: null,
			}
		}
	}

	/**
	 * Assign a CRM product to an Infakt invoice line item
	 * PUT /api/infakt/assigncorrectionproduct/:korektaId/:productIndex/:productId
	 * @param {string} korektaId
	 * @param {number|string} productIndex - 0-based line index
	 * @param {string} productId
	 * @returns {Promise<{success: boolean, error: string|null, data: *}>}
	 */
	async assignProduct(korektaId, productIndex, productId) {
		try {
			if (!korektaId || productIndex === null || productIndex === undefined || productIndex === '' || !productId) {
				return {
					success: false,
					error: 'Korekta ID, product index and product ID are required',
					data: null,
				}
			}

			const url = `${this.endpoints.assignProduct}/${encodeURIComponent(korektaId)}/${encodeURIComponent(productIndex)}/${encodeURIComponent(productId)}`
			const response = await Axhelper.sendPutAsync(url, null)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KorektyService.assignProduct error:', error)
			return {
				success: false,
				error: error.message || 'Failed to assign product to invoice line',
				data: null,
			}
		}
	}

	/**
	 * Get invoices by customer
	 * @param {string} customerId - Customer ID
	 * @returns {Promise<{data: Array, error: string|null}>}
	 */
	async getInvoicesByCustomer(customerId) {
		try {
			if (!customerId) {
				return {
					data: [],
					error: 'Invalid customer ID provided',
				}
			}

			const url = `${this.endpoints.invoicesByCustomer}/${encodeURIComponent(customerId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
				}
			}

			return {
				data: response.data || [],
				error: null,
			}
		} catch (error) {
			console.error('KorektyService.getInvoicesByCustomer error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch korektas by customer',
			}
		}
	}

	async downloadKorektaPDF(korektaId) {
		try {
			if (!korektaId) {
				return { success: false, error: 'Invalid korekta ID', blob: null, filename: null }
			}

			const url = `${this.endpoints.invoicesDownload}/${encodeURIComponent(korektaId)}`
			const response = await this._downloadBlob(url)
			console.log("TCL -> KorektyService -> downloadKorektaPDF -> response:", response)

			if (response.error) {
				return { success: false, error: response.error, blob: null, filename: null }
			}

			const filename = this._extractFilename(response.headers, korektaId)
			return { success: true, error: null, blob: response.blob, filename }
		} catch (error) {
			console.error('KorektyService.downloadKorektaPDF error:', error)
			return { success: false, error: error.message || 'Failed to download PDF', blob: null, filename: null }
		}
	}

	/**
	 * Download blob data using axios with credentials
	 * @private
	 */
	async _downloadBlob(url) {
		try {
			const axios = (await import('axios')).default
			const response = await axios.get(url, {
				responseType: 'blob',
				withCredentials: true,
				headers: { Accept: 'application/pdf' }
			})

			return { 
				error: null, 
				blob: response.data, 
				headers: response.headers 
			}
		} catch (error) {
			const errorMessage = error.response?.data || error.message || 'Download failed'
			return { error: errorMessage, blob: null, headers: null }
		}
	}

	/**
	 * Extract filename from Content-Disposition header
	 * @private
	 */
	_extractFilename(headers, korektaId) {
		const defaultFilename = `invoice-${korektaId}.pdf`
		
		if (!headers) return defaultFilename
		
		const disposition = headers['content-disposition'] || ''
		
		// Try UTF-8 encoded filename first
		const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
		if (utf8Match?.[1]) {
			return decodeURIComponent(utf8Match[1])
		}
		
		// Fallback to regular filename
		const regularMatch = disposition.match(/filename="?([^";]+)"?/i)
		if (regularMatch?.[1]) {
			return regularMatch[1]
		}
		
		return defaultFilename
	}

	/**
	 * Build URL for filtered invoice requests
	 * @private
	 */
	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		let params = [`limit=${limit}`, `sortcol=${sortColumn}`, `sortdir=${sortDirection}`]

		// Add search text filter
		if (filters.searchText && filters.searchText.length >= 3) {
			params.push(`searchText=${encodeURIComponent(filters.searchText)}`)
		}

		// Add status filter
		if (filters.status) {
			params.push(`status=${encodeURIComponent(filters.status)}`)
		}

		// Add date filters
		if (filters.fromDate) {
			params.push(`fromDate=${encodeURIComponent(filters.fromDate)}`)
		}

		if (filters.toDate) {
			params.push(`toDate=${encodeURIComponent(filters.toDate)}`)
		}

		if (filters.companyId) {
			params.push(`companyId=${encodeURIComponent(filters.companyId)}`)
		}

		return `${this.endpoints.invoices}?${params.join('&')}`
	}
}

// Export a singleton instance
export default new KorektyService()
